import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Code,
  ArrowLeft,
  Rocket,
  Share2,
  Bot,
  Zap,
  Settings,
  Database,
  GitBranch,
  AlertTriangle,
  LogOut,
  ChevronDown,
  RefreshCw,
  Lock,
  Lightbulb
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

// =============================================
// DEVELOP LAYOUT - Avec Sidebar dédiée
// Zone de développement V2
// =============================================

const DevelopLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    features: true,
    tools: true,
    config: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const developNavigation = [
    {
      section: 'features',
      label: 'Fonctionnalités V2',
      icon: Rocket,
      color: '#8b5cf6',
      items: [
        { to: '/develop', icon: Rocket, label: 'Vue d\'ensemble', end: true },
        { to: '/develop/copilote', icon: Lightbulb, label: 'Copilote', status: 'dev' },
        { to: '/develop/social', icon: Share2, label: 'Réseaux Sociaux', status: 'planned' },
        { to: '/develop/assistant', icon: Bot, label: 'Assistant IA', status: 'planned' },
        { to: '/develop/marketing', icon: Zap, label: 'Outils Marketing', status: 'partial' },
      ]
    },
    {
      section: 'tools',
      label: 'Outils Dev',
      icon: Code,
      color: '#ec4899',
      items: [
        { to: '/develop/database', icon: Database, label: 'Base de données' },
        { to: '/develop/migrations', icon: RefreshCw, label: 'Migrations' },
        { to: '/develop/git', icon: GitBranch, label: 'Branches' },
      ]
    },
    {
      section: 'config',
      label: 'Configuration',
      icon: Settings,
      color: '#f59e0b',
      items: [
        { to: '/develop/settings', icon: Settings, label: 'Paramètres V2' },
        { to: '/develop/access', icon: Lock, label: 'Accès Beta' },
      ]
    },
  ];

  const statusColors = {
    dev: 'bg-amber-500/20 text-amber-300',
    planned: 'bg-slate-500/20 text-slate-400',
    partial: 'bg-emerald-500/20 text-emerald-300',
  };

  const renderNavItem = (item, sectionColor) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm",
            isActive
              ? "bg-violet-500/20 text-violet-200 font-medium"
              : "text-white/60 hover:text-white hover:bg-white/5"
          )
        }
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1">{item.label}</span>
        {item.status && (
          <span className={cn("text-xs px-1.5 py-0.5 rounded", statusColors[item.status])}>
            {item.status === 'dev' ? 'Dev' : item.status === 'planned' ? 'Prévu' : 'Partiel'}
          </span>
        )}
      </NavLink>
    );
  };

  const renderSection = (section) => {
    const Icon = section.icon;
    const isExpanded = expandedSections[section.section];
    
    return (
      <div key={section.section} className="mb-2">
        <button
          onClick={() => toggleSection(section.section)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all"
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4" style={{ color: section.color }} />
            <span className="text-sm font-medium">{section.label}</span>
          </div>
          <ChevronDown className={cn("w-4 h-4 transition-transform", isExpanded && "rotate-180")} />
        </button>
        
        {isExpanded && (
          <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
            {section.items.map(item => renderNavItem(item, section.color))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Sidebar DEVELOP */}
      <aside className="fixed top-0 left-0 h-full w-64 z-40 flex flex-col bg-gradient-to-b from-violet-900 to-violet-950">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
              <Code className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-white font-bold text-lg">DEVELOP</h1>
                <Badge className="bg-violet-500/30 text-violet-200 text-xs">V2</Badge>
              </div>
              <p className="text-white/50 text-xs">Zone développement</p>
            </div>
          </div>
        </div>

        {/* Back to V1 */}
        <div className="p-3 border-b border-white/10">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard/agency')}
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour Espace Client
          </Button>
        </div>

        {/* Warning */}
        <div className="p-3 mx-3 mt-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/80">Zone de test. Les fonctionnalités peuvent être instables.</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {developNavigation.map(section => renderSection(section))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.first_name?.[0] || 'D'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.first_name}</p>
              <p className="text-white/50 text-xs truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white/50 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="ml-64">
        {/* Simple Header */}
        <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold text-[#1a365d]">Développement V2</h2>
            <Badge variant="outline" className="text-violet-600 border-violet-300">Beta</Badge>
          </div>
          <span className="text-sm text-slate-500">{user?.first_name}</span>
        </header>
        
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DevelopLayout;
